"use client"

import { Search, Plus, RefreshCw, Brain } from "lucide-react"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import type { SemanticMemory } from "@/lib/memory/semantic/types"
import { cn } from "@/lib/utils"

interface MemoryListProps {
  memories: SemanticMemory[]
  isLoading: boolean
  searchQuery: string
  selectedCategory: string | null
  selectedMemoryId?: string
  onSearchChange: (query: string) => void
  onCategoryChange: (category: string | null) => void
  onSelectMemory: (memory: SemanticMemory) => void
  onRefresh: () => void
}

const MEMORY_CATEGORIES = [
  { id: "company", label: "Company Context", color: "var(--brain)" },
  { id: "goals", label: "Goals & Objectives", color: "var(--goals)" },
  { id: "decisions", label: "Past Decisions", color: "var(--strategy)" },
  { id: "market", label: "Market Intelligence", color: "var(--text-accent)" },
  { id: "resources", label: "Team & Resources", color: "var(--pulse)" },
]

export function MemoryList({
  memories,
  isLoading,
  searchQuery,
  selectedCategory,
  selectedMemoryId,
  onSearchChange,
  onCategoryChange,
  onSelectMemory,
  onRefresh,
}: MemoryListProps) {
  const filteredMemories = memories.filter((memory) => {
    const matchesSearch =
      !searchQuery ||
      memory.label?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      memory.content?.toLowerCase().includes(searchQuery.toLowerCase())

    const matchesCategory =
      !selectedCategory || memory.storage_section === selectedCategory || memory.domains?.includes(selectedCategory)

    return matchesSearch && matchesCategory && !memory.is_archived
  })

  const memoriesBySection = filteredMemories.reduce(
    (acc, memory) => {
      const section = memory.storage_section || "uncategorized"
      if (!acc[section]) acc[section] = []
      acc[section].push(memory)
      return acc
    },
    {} as Record<string, typeof memories>,
  )

  return (
    <div className="flex flex-col h-full bg-background p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-semibold">Brain</h1>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={onRefresh} disabled={isLoading}>
            <RefreshCw className={cn("h-4 w-4", isLoading && "animate-spin")} />
          </Button>
          <Button variant="default" size="sm" className="bg-[var(--brain)] hover:bg-[var(--brain)]/90">
            <Plus className="h-4 w-4 mr-1" />
            Add Memory
          </Button>
        </div>
      </div>

      {/* Search & Filter */}
      <div className="space-y-3 mb-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search memories..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            className="pl-9"
          />
        </div>

        {/* Category filters */}
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant={selectedCategory === null ? "default" : "outline"}
            size="sm"
            onClick={() => onCategoryChange(null)}
            className="h-7 text-xs"
          >
            All
          </Button>
          {MEMORY_CATEGORIES.map((category) => (
            <Button
              key={category.id}
              variant={selectedCategory === category.id ? "default" : "outline"}
              size="sm"
              onClick={() => onCategoryChange(category.id)}
              className="h-7 text-xs"
              style={
                selectedCategory === category.id
                  ? {
                      backgroundColor: category.color,
                      borderColor: category.color,
                    }
                  : undefined
              }
            >
              {category.label}
            </Button>
          ))}
        </div>
      </div>

      {/* Memory List - Grouped by Section */}
      <div className="flex-1 overflow-y-auto space-y-6">
        {isLoading ? (
          <div className="flex items-center justify-center h-32 text-muted-foreground">Loading memories...</div>
        ) : filteredMemories.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full space-y-4 px-4">
            <div className="h-12 w-12 rounded-full bg-[var(--brain)]/10 flex items-center justify-center">
              <Brain className="h-6 w-6 text-[var(--brain)]" />
            </div>
            <div className="text-center space-y-2">
              <h3 className="font-medium">No memories yet</h3>
              <p className="text-sm text-muted-foreground max-w-sm">
                Start a conversation with the Advisor to save your first memory
              </p>
            </div>
            <div className="space-y-2 w-full max-w-xs">
              <p className="text-xs text-muted-foreground text-center">Suggested prompts:</p>
              <div className="space-y-1">
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full justify-start text-xs h-auto py-2 px-3 bg-transparent"
                >
                  My business is called...
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full justify-start text-xs h-auto py-2 px-3 bg-transparent"
                >
                  We serve customers in...
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full justify-start text-xs h-auto py-2 px-3 bg-transparent"
                >
                  My main goal this quarter is...
                </Button>
              </div>
            </div>
          </div>
        ) : (
          Object.entries(memoriesBySection).map(([section, sectionMemories]) => (
            <div key={section} className="space-y-2">
              <div className="flex items-center gap-2 px-1">
                <h3 className="text-sm font-medium text-muted-foreground capitalize">{section}</h3>
                <span className="text-xs text-muted-foreground">({sectionMemories.length})</span>
              </div>
              <div className="space-y-2">
                {sectionMemories.map((memory) => (
                  <button
                    key={memory.id}
                    onClick={() => onSelectMemory(memory)}
                    className={cn(
                      "w-full text-left transition-all p-3 rounded-lg border",
                      "hover:bg-accent/50 hover:border-accent",
                      selectedMemoryId === memory.id && "ring-2 ring-[var(--brain)] border-[var(--brain)]",
                    )}
                  >
                    <div className="space-y-1">
                      <p className="text-sm font-medium">{memory.label}</p>
                      <p className="text-xs text-muted-foreground line-clamp-2">{memory.content}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  )
}
